import { registerComponent, debounce, getComponentConfig } from "helpers";

registerComponent(
  "wyatt-slider",
  class WyattSlider extends HTMLElement {
    constructor() {
      super();

      this.config = getComponentConfig(this);
      this.currentIndex = 0;
      this.slider = this.querySelector("[data-slider]");
      this.slides = Array.from(this.slider.children);
      this.slideCount = this.slides.length;
      this.showControls = this.config.hideControls
        ? false
        : this.slideCount > 1 && this.slideCount > this.config.slidesToShow;
    }

    connectedCallback() {
      this.setupData();
      this.addNavigation();
      this.addControls();
      this.bindEvents();
      this.connectSliders();

      this.goTo(this.config.startAt, "instant");
      this.setAttribute("ready", true);
    }

    // SETUP

    setupData() {
      this.slides.forEach((slide, index) => {
        slide.dataset.slide = index;
        slide.classList.add("slider__slide");
      });
    }

    addControls() {
      if (!this.showControls) {
        return;
      }

      const template = this.querySelector("[data-controls]").content;
      const controls = document.importNode(template, true);
      this.appendChild(controls);

      this.setAttribute("has-controls", true);
      this.controlButtons = this.querySelectorAll(
        "[data-slider-control-button]"
      );
    }

    addNavigation() {
      if (!this.config.showNavigation) {
        return;
      }

      const navigation = document.createElement("div");
      const navigationButton = this.querySelector(
        "[data-navigation-button]"
      ).content;

      this.slides.forEach((slide, index) => {
        const button = navigationButton.cloneNode(true);

        button.querySelector(
          "[data-slider-navigation-button]"
        ).dataset.sliderNavigationButton = index;

        navigation.appendChild(button);
      });

      navigation.classList.add("slider__navigation");
      this.appendChild(navigation);

      this.navigationButtons = navigation.querySelectorAll(
        "[data-slider-navigation-button]"
      );

      this.navigationButtons.forEach((button, index) =>
        button.setAttribute("aria-label", `Slide ${index + 1}`)
      );
    }

    bindEvents() {
      this.slider.addEventListener(
        "scroll",
        debounce((event) => {
          this.handleScroll(event);
        }, 300).bind(this)
      );

      if (this.showControls) {
        this.controlButtons.forEach((button) => {
          button.addEventListener("click", this.handleControlClick.bind(this));
        });
      }

      if (this.config.showNavigation) {
        this.navigationButtons.forEach((button) => {
          button.addEventListener(
            "click",
            this.handleNavigationClick.bind(this)
          );
        });
      }
    }

    connectSliders() {
      const sliderToNavigate = document.querySelector(
        this.config.navigationFor
      );

      if (sliderToNavigate) {
        this.sliderToNavigate = sliderToNavigate;
        sliderToNavigate.navigatedBy = this;

        this.slides.forEach((slide) => {
          this.updateSlideAsNavigation(slide);

          slide
            .querySelector("button")
            .addEventListener(
              "click",
              this.handleSlideAsNavigationClick.bind(this)
            );
        });
      }
    }

    updateSlideAsNavigation(slide) {
      const button = document.createElement("button");
      const html = slide.innerHTML;

      button.type = "button";
      button.classList.add("button-reset", "slider__slide-button");
      button.innerHTML = html;

      slide.innerHTML = "";
      slide.appendChild(button);
    }

    // HANDLERS

    handleControlClick(event) {
      const button = event.currentTarget;
      const direction = button.dataset.sliderControlButton;
      const newIndex =
        direction === "next" ? this.currentIndex + 1 : this.currentIndex - 1;

      // Thumbnail strip controls the main gallery: scroll primary only and
      // update local state. Thumb sync repositions the strip after the transition.
      if (this.sliderToNavigate) {
        this.sliderToNavigate.goTo(newIndex);
        this.updateState(newIndex);
        return;
      }

      this.goTo(newIndex);

      if (this.navigatedBy) {
        this.navigatedBy.goTo(newIndex);
      }
    }

    handleNavigationClick(event) {
      const button = event.currentTarget;
      const newIndex = parseInt(button.dataset.sliderNavigationButton, 10);

      this.goTo(newIndex);
    }

    handleScroll(event) {
      if (this === this.sliderToNavigate?.navigatedBy) {
        return;
      }

      if (this._programmaticScroll) {
        return;
      }

      /*
        scrollLeft and offsetWidth round the actual values, therefor a buffer is needed
        to ensure the position will be slightly larger than the multiples of slideWidth
      */
      const buffer = this.slideCount;
      const position = this.slider.scrollLeft + buffer;
      const slideWidth = this.slider.querySelector(
        "[data-slide]:not(.hidden)"
      ).offsetWidth;
      const newIndex = Math.floor(position / slideWidth);

      if (newIndex === this.currentIndex) {
        return;
      }

      event.preventDefault();

      this.updateState(newIndex);

      if (this.navigatedBy) {
        this.navigatedBy.goTo(newIndex);
      }
    }

    handleSlideAsNavigationClick(event) {
      const slide = event.currentTarget.closest("[data-slide]");
      const index = parseInt(slide.dataset.slide, 10);

      event.preventDefault();

      this.sliderToNavigate.goTo(index);
      this.updateState(index);
    }

    // UPDATES

    updateState(newIndex) {
      this.updateSliderClass(newIndex);
      this.updateNavigationButtons(newIndex);
      this.updateCurrentSlide(newIndex);
      this.updateControlButtons();
    }

    updateSliderClass(newIndex) {
      this.classList.remove(`slider--at-slide-${this.currentIndex}`);
      this.classList.add(`slider--at-slide-${newIndex}`);
    }

    updateNavigationButtons(newIndex) {
      if (!this.config.showNavigation) {
        return;
      }

      const allButtons = Array.from(
        this.querySelectorAll("[data-slider-navigation-button]")
      );
      const buttonsToActivate = allButtons.slice(
        newIndex,
        newIndex + this.config.slidesToShow
      );

      allButtons.forEach((button) => button.setAttribute("active", false));
      buttonsToActivate.forEach((button) =>
        button.setAttribute("active", true)
      );
    }

    updateCurrentSlide(newIndex) {
      this.slides[this.currentIndex].setAttribute("active", false);
      this.currentIndex = newIndex;
      this.slides[this.currentIndex].setAttribute("active", true);
    }

    updateControlButtons() {
      if (!this.showControls) {
        return;
      }

      const nextSlideCount = () => {
        const lastShownSlide = this.currentIndex + this.config.slidesToShow;

        return this.slideCount - lastShownSlide < 0
          ? 0
          : this.slideCount - lastShownSlide;
      };

      this.controlButtons.forEach((button) => {
        const direction = button.dataset.sliderControlButton;
        const onFirstSlide = this.currentIndex === 0;
        const onLastSlide = this.currentIndex + 1 === this.slideCount;

        if (direction === "previous" && onFirstSlide) {
          button.disabled = true;
        } else if (
          (direction === "next" && onLastSlide) ||
          (direction === "next" &&
            nextSlideCount() === 0 &&
            !this.sliderToNavigate)
        ) {
          button.disabled = true;
        } else {
          button.disabled = false;
        }
      });
    }

    goTo(newIndex, behavior = "smooth") {
      const newSlide = this.slider.querySelector(`[data-slide="${newIndex}"]`);
      if (!newSlide) {
        return;
      }

      const left = newSlide.offsetLeft;

      if (behavior === "smooth") {
        this.slider.style.scrollSnapType = "none";
        this._programmaticScroll = true;

        const onScrollEnd = () => {
          this.slider.style.scrollSnapType = "";
          this._programmaticScroll = false;
        };

        this.slider.addEventListener("scrollend", onScrollEnd, { once: true });
        setTimeout(onScrollEnd, 500);
      }

      this.slider.scrollTo({
        top: 0,
        left,
        behavior,
      });

      this.updateState(newIndex);
    }
  }
);
